import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Trash2, Edit2, Save, Search, Filter, 
  Eye, EyeOff, Copy, RotateCcw, Upload, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const ContentCMS = ({ i18n }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPage, setSelectedPage] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingKey, setEditingKey] = useState(null);

  const [content, setContent] = useState({});
  const [filteredContent, setFilteredContent] = useState([]);

  const pages = [
    { value: 'all', label: i18n.language === 'ar' ? 'كل الصفحات' : 'All Pages' },
    { value: 'landing', label: i18n.language === 'ar' ? 'الصفحة الرئيسية' : 'Landing Page' },
    { value: 'pricing', label: i18n.language === 'ar' ? 'صفحة الأسعار' : 'Pricing Page' },
    { value: 'dashboard', label: i18n.language === 'ar' ? 'لوحة التحكم' : 'Dashboard' },
    { value: 'lessons', label: i18n.language === 'ar' ? 'الدروس' : 'Lessons' },
    { value: 'tests', label: i18n.language === 'ar' ? 'الاختبارات' : 'Tests' },
    { value: 'support', label: i18n.language === 'ar' ? 'الدعم' : 'Support' },
    { value: 'footer', label: i18n.language === 'ar' ? 'Footer' : 'Footer' },
    { value: 'navbar', label: i18n.language === 'ar' ? 'Navbar' : 'Navbar' }
  ];

  const categories = [
    { value: 'all', label: i18n.language === 'ar' ? 'كل الفئات' : 'All Categories' },
    { value: 'headings', label: i18n.language === 'ar' ? 'عناوين' : 'Headings' },
    { value: 'descriptions', label: i18n.language === 'ar' ? 'أوصاف' : 'Descriptions' },
    { value: 'buttons', label: i18n.language === 'ar' ? 'أزرار' : 'Buttons' },
    { value: 'labels', label: i18n.language === 'ar' ? 'تسميات' : 'Labels' },
    { value: 'messages', label: i18n.language === 'ar' ? 'رسائل' : 'Messages' },
    { value: 'placeholders', label: i18n.language === 'ar' ? 'نصوص تلقائية' : 'Placeholders' }
  ];

  useEffect(() => {
    loadContent();
  }, []);

  useEffect(() => {
    filterContent();
  }, [content, searchTerm, selectedPage, selectedCategory]);

  const loadContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/content-cms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error(i18n.language === 'ar' ? 'فشل تحميل المحتوى' : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const filterContent = () => {
    let entries = [];
    
    Object.keys(content).forEach(page => {
      // Skip metadata fields
      if (page === 'id' || page === '_id' || page === 'updated_at' || page === 'updated_by') return;
      
      if (selectedPage !== 'all' && page !== selectedPage) return;
      
      // Make sure page content exists and is an object
      if (!content[page] || typeof content[page] !== 'object') return;
      
      Object.keys(content[page]).forEach(category => {
        if (selectedCategory !== 'all' && category !== selectedCategory) return;
        
        // Make sure category content exists and is an object
        if (!content[page][category] || typeof content[page][category] !== 'object') return;
        
        Object.keys(content[page][category]).forEach(key => {
          const value = content[page][category][key];
          
          // Make sure value is an object with en/ar properties
          if (!value || typeof value !== 'object') return;
          
          if (searchTerm && !key.toLowerCase().includes(searchTerm.toLowerCase()) && 
              !(value.en || '').toLowerCase().includes(searchTerm.toLowerCase()) &&
              !(value.ar || '').toLowerCase().includes(searchTerm.toLowerCase())) {
            return;
          }
          
          entries.push({
            page,
            category,
            key,
            value
          });
        });
      });
    });
    
    setFilteredContent(entries);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/content-cms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save content');
      }

      toast.success(i18n.language === 'ar' ? 'تم حفظ المحتوى بنجاح! ✅' : 'Content saved successfully! ✅');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error(i18n.language === 'ar' ? `فشل الحفظ: ${error.message}` : `Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (page, category, key, lang, value) => {
    setContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [category]: {
          ...prev[page]?.[category],
          [key]: {
            ...prev[page]?.[category]?.[key],
            [lang]: value
          }
        }
      }
    }));
  };

  const addNewEntry = () => {
    const key = prompt(i18n.language === 'ar' ? 'أدخل المفتاح (key):' : 'Enter key:');
    if (!key) return;
    
    const page = selectedPage === 'all' ? 'landing' : selectedPage;
    const category = selectedCategory === 'all' ? 'headings' : selectedCategory;
    
    setContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [category]: {
          ...prev[page]?.[category],
          [key]: {
            en: '',
            ar: ''
          }
        }
      }
    }));
    
    toast.success(i18n.language === 'ar' ? 'تمت إضافة العنصر' : 'Entry added');
  };

  const deleteEntry = (page, category, key) => {
    if (!window.confirm(i18n.language === 'ar' ? 'هل تريد حذف هذا العنصر؟' : 'Delete this entry?')) return;
    
    const newContent = { ...content };
    delete newContent[page][category][key];
    setContent(newContent);
    
    toast.success(i18n.language === 'ar' ? 'تم الحذف' : 'Deleted');
  };

  const exportContent = () => {
    const dataStr = JSON.stringify(content, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = 'content-cms-export.json';
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success(i18n.language === 'ar' ? 'تم التصدير' : 'Exported');
  };

  const importContent = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        setContent(imported);
        toast.success(i18n.language === 'ar' ? 'تم الاستيراد' : 'Imported');
      } catch (error) {
        toast.error(i18n.language === 'ar' ? 'فشل الاستيراد' : 'Import failed');
      }
    };
    reader.readAsText(file);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-slate-600" />
            {i18n.language === 'ar' ? 'نظام إدارة المحتوى الشامل' : 'Complete Content Management System'}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {i18n.language === 'ar' 
              ? 'تحكم كامل في كل نص في الموقع - أضف، عدل، احذف أي محتوى'
              : 'Complete control over every text in the website - Add, edit, delete any content'
            }
          </p>
        </div>
        
        <div className="flex gap-2">
          <label htmlFor="import-file">
            <Button variant="outline" className="cursor-pointer" asChild>
              <span>
                <Upload className="w-4 h-4 me-2" />
                {i18n.language === 'ar' ? 'استيراد' : 'Import'}
              </span>
            </Button>
          </label>
          <input
            id="import-file"
            type="file"
            accept=".json"
            onChange={importContent}
            className="hidden"
          />
          
          <Button variant="outline" onClick={exportContent}>
            <Download className="w-4 h-4 me-2" />
            {i18n.language === 'ar' ? 'تصدير' : 'Export'}
          </Button>
          
          <Button onClick={addNewEntry} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 me-2" />
            {i18n.language === 'ar' ? 'إضافة جديد' : 'Add New'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>{i18n.language === 'ar' ? 'بحث' : 'Search'}</Label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={i18n.language === 'ar' ? 'ابحث في المحتوى...' : 'Search content...'}
                className="ps-10"
              />
            </div>
          </div>
          
          <div>
            <Label>{i18n.language === 'ar' ? 'الصفحة' : 'Page'}</Label>
            <Select value={selectedPage} onValueChange={setSelectedPage}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pages.map(page => (
                  <SelectItem key={page.value} value={page.value}>
                    {page.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>{i18n.language === 'ar' ? 'الفئة' : 'Category'}</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('');
                setSelectedPage('all');
                setSelectedCategory('all');
              }}
              className="w-full"
            >
              <RotateCcw className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'إعادة تعيين' : 'Reset'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Content List */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-600">
            {i18n.language === 'ar' 
              ? `عرض ${filteredContent.length} عنصر`
              : `Showing ${filteredContent.length} items`
            }
          </p>
        </div>

        {filteredContent.map((entry, index) => (
          <Card key={`${entry.page}-${entry.category}-${entry.key}`} className="p-4 hover:shadow-md transition-shadow">
            <div className="space-y-3">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                      {entry.page}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                      {entry.category}
                    </span>
                  </div>
                  <p className="font-mono text-sm text-slate-600">{entry.key}</p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingKey(editingKey === entry.key ? null : entry.key)}
                  >
                    {editingKey === entry.key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteEntry(entry.page, entry.category, entry.key)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              {editingKey === entry.key && (
                <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                  <div>
                    <Label className="text-xs">English</Label>
                    <Textarea
                      value={entry.value.en || ''}
                      onChange={(e) => updateContent(entry.page, entry.category, entry.key, 'en', e.target.value)}
                      className="mt-1 min-h-[80px]"
                      placeholder="English text..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">العربية</Label>
                    <Textarea
                      value={entry.value.ar || ''}
                      onChange={(e) => updateContent(entry.page, entry.category, entry.key, 'ar', e.target.value)}
                      className="mt-1 min-h-[80px]"
                      placeholder="النص العربي..."
                      dir="rtl"
                    />
                  </div>
                </div>
              )}
              
              {editingKey !== entry.key && (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="text-slate-700">
                    <span className="font-semibold text-xs text-slate-500">EN:</span> {entry.value.en || '-'}
                  </div>
                  <div className="text-slate-700" dir="rtl">
                    <span className="font-semibold text-xs text-slate-500">AR:</span> {entry.value.ar || '-'}
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}

        {filteredContent.length === 0 && (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">
              {i18n.language === 'ar' ? 'لا توجد نتائج' : 'No results found'}
            </p>
          </Card>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3 border-t border-slate-200 pt-6 sticky bottom-0 bg-white pb-4">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          size="lg"
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          {saving ? (
            <>
              <div className="w-5 h-5 me-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              {i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
            </>
          ) : (
            <>
              <Save className="w-5 h-5 me-2" />
              {i18n.language === 'ar' ? 'حفظ جميع التغييرات' : 'Save All Changes'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ContentCMS;
